import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronDown, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const CURRENCIES = [
  { code: 'INR', symbol: '₹',   label: 'Indian Rupee' },
  { code: 'USD', symbol: '$',   label: 'US Dollar' },
  { code: 'EUR', symbol: '€',   label: 'Euro' },
  { code: 'GBP', symbol: '£',   label: 'British Pound' },
  { code: 'AED', symbol: 'AED', label: 'UAE Dirham' },
  { code: 'SAR', symbol: 'SAR', label: 'Saudi Riyal' },
  { code: 'IDR', symbol: 'Rp',  label: 'Indonesian Rupiah' },
  { code: 'THB', symbol: '฿',   label: 'Thai Baht' },
  { code: 'CNY', symbol: '¥',   label: 'Chinese Yuan' },
];

const FALLBACK_RATES = { INR: 1, USD: 0.012, EUR: 0.011, GBP: 0.0095, AED: 0.044, SAR: 0.045, IDR: 188.0, THB: 0.42, CNY: 0.086 };

let _ratesCache = null;
let _ratesPromise = null;
function fetchRates() {
  if (_ratesCache) return Promise.resolve(_ratesCache);
  if (_ratesPromise) return _ratesPromise;
  _ratesPromise = fetch(`${API}/api/exchange-rates`)
    .then(r => r.json())
    .then(d => { _ratesCache = d.rates || {}; return _ratesCache; })
    .catch(() => ({}))
    .finally(() => { _ratesPromise = null; });
  return _ratesPromise;
}

/** Hook: returns { code, rate } — rate is the INR→code multiplier (1 for INR).
 *  Default currency is always INR unless the user manually picked another one (persisted).
 *  Responds to 'biomall_currency_change' window event so all instances update together.
 */
export function useCurrency() {
  const [code, setCode] = useState(() => localStorage.getItem('biomall_currency') || 'INR');
  const [rates, setRates] = useState(_ratesCache);

  useEffect(() => {
    const handler = () => setCode(localStorage.getItem('biomall_currency') || 'INR');
    window.addEventListener('biomall_currency_change', handler);
    return () => window.removeEventListener('biomall_currency_change', handler);
  }, []);

  useEffect(() => {
    if (code === 'INR' || rates) return;
    let mounted = true;
    fetchRates().then(r => { if (mounted) setRates(r); });
    return () => { mounted = false; };
  }, [code, rates]);

  return useMemo(() => ({
    code,
    rate: code === 'INR' ? 1 : ((rates && rates[code]) || FALLBACK_RATES[code] || 1),
  }), [code, rates]);
}

export function extractInrValue(raw) {
  const trimmed = (raw || '').toString().trim();
  const BAD = ['nan', 'n/a', 'na', '0', ''];
  if (BAD.includes(trimmed.toLowerCase())) return null;
  const numStr = trimmed.replace(/[₹,\s]/g, '').replace(/^(INR|Rs\.?)/i, '').match(/[\d.]+/)?.[0];
  if (!numStr) return null;
  const val = parseFloat(numStr);
  return (!val || val <= 0) ? null : val;
}

function formatInCurrency(val, code) {
  const meta = CURRENCIES.find(c => c.code === code) || CURRENCIES[0];
  const decimals = (code === 'INR' || code === 'IDR') ? 0 : 2;
  const formatted = val.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return `${meta.symbol}${formatted}`;
}

/** Avoid "AED AED" / "SAR SAR" when a currency's symbol has no distinct glyph (equals its code). */
function curLabel(c) {
  return c.symbol === c.code ? c.code : `${c.symbol} ${c.code}`;
}

/** Convert + format a raw INR price string for display. Returns null if unusable. */
export function displayPrice(raw, currencyInfo) {
  const inrVal = extractInrValue(raw);
  if (inrVal === null) return null;
  const code = currencyInfo?.code || 'INR';
  if (code === 'INR') return formatInCurrency(inrVal, 'INR');
  return formatInCurrency(inrVal * (currencyInfo?.rate || 1), code);
}

/** Returns an indicative ±20% price range string, rounded, with a suffix (e.g. "indicative" / "approx."). */
export function indicativePriceRange(raw, currencyInfo, suffix = 'indicative') {
  const val = extractInrValue(raw);
  if (val === null) return null;
  const code = currencyInfo?.code || 'INR';
  const rate = code === 'INR' ? 1 : (currencyInfo?.rate || 1);
  const lo = val * 0.8 * rate;
  const hi = val * 1.2 * rate;
  function rnd(n) {
    if (n < 500)  return Math.round(n / 10) * 10;
    if (n < 5000) return Math.round(n / 50) * 50;
    return Math.round(n / 500) * 500;
  }
  return `${formatInCurrency(rnd(lo), code)} – ${formatInCurrency(rnd(hi), code)} ${suffix}`;
}

/** Dropdown to pick display currency. Persisted to localStorage + broadcast via window event. */
export function CurrencySelector({ className = '' }) {
  const [code, setCodeState] = useState(() => localStorage.getItem('biomall_currency') || 'INR');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (c) => {
    localStorage.setItem('biomall_currency', c);
    setCodeState(c);
    setOpen(false);
    window.dispatchEvent(new Event('biomall_currency_change'));
  };

  const current = CURRENCIES.find(c => c.code === code) || CURRENCIES[0];

  return (
    <div className={`relative ${className}`} ref={ref} data-testid="currency-selector">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="currency-selector-btn"
        className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 rounded-full pl-2.5 pr-1.5 py-1 text-xs font-bold text-gray-700 transition-colors"
      >
        {curLabel(current)}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1.5 w-44 bg-white border border-gray-200 rounded-xl shadow-xl z-50 py-1 max-h-72 overflow-y-auto">
          {CURRENCIES.map(c => (
            <button
              key={c.code}
              type="button"
              onClick={() => pick(c.code)}
              data-testid={`currency-option-${c.code.toLowerCase()}`}
              className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs text-left transition-colors ${
                code === c.code ? 'text-blue-600 font-bold bg-blue-50' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span className="font-semibold">{curLabel(c)}</span>
              <span className="text-gray-400 truncate">{c.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Small (i) icon with hover tooltip explaining that list prices exclude freight/tax.
 *  Renders below-right of the icon (not above) so it isn't clipped by scrollable list containers.
 */
export function PriceInfoTooltip({ text = 'This price excludes freight and taxes. Please RFQ to get the final price.', className = '' }) {
  return (
    <span className={`relative inline-flex items-center group ml-1 ${className}`} data-testid="price-info-tooltip">
      <Info className="w-3 h-3 text-gray-400 hover:text-gray-600 cursor-help" />
      <span className="pointer-events-none absolute right-0 top-full mt-2 w-48 rounded-lg bg-gray-900 text-white text-[11px] leading-snug px-2.5 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-[999] text-center shadow-lg">
        {text}
        <span className="absolute right-1.5 bottom-full w-1.5 h-1.5 bg-gray-900 rotate-45" />
      </span>
    </span>
  );
}
